import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CallEntity } from './call.entity';
import { SoftphoneDeviceEntity } from './softphone-device.entity';
import { UserEntity } from './user.entity';

/**
 * What a rep's leg is doing on a call (PD-14897).
 *   - `owner`    — the leg that holds the call: it is the rep's audio, its
 *                  hang-up ends the call, and its drop parks the customer.
 *   - `listener` — a second device that joined the audio. It joins muted,
 *                  and its coming or going never ends or parks the call.
 */
export type SoftphoneCallDeviceLegRoleModel = 'owner' | 'listener';

/**
 * Lifecycle of a rep leg (PD-14897).
 *   - `pending`    — a claim was issued; the device has not joined yet.
 *   - `active`     — the leg is on the call.
 *   - `parked`     — the owner leg dropped without the rep hanging up (a
 *                    reload, a lost network). The customer is held for the
 *                    grace window in `parkedUntil`.
 *   - `superseded` — ownership moved to another device; this leg was (or
 *                    is being) removed, and its end must not end the call.
 *   - `left`       — the leg is gone.
 */
export type SoftphoneCallDeviceLegStatusModel =
	| 'pending'
	| 'active'
	| 'parked'
	| 'superseded'
	| 'left';

/**
 * One row per rep leg of a softphone call, tied to the device it runs on
 * (PD-14897). This is the server-side authority for call ownership across
 * a rep's devices: which device owns the call, which devices only listen,
 * and whether the rep meant to hang up.
 *
 * Written by:
 *   - the inbound answer (`confirm-rep-leg`) and the outbound voice
 *     webhook — the call's first owner leg;
 *   - a claim (transfer here / join audio) — a `pending` leg that turns
 *     `active` when its conference participant joins;
 *   - the hang-up button (`endRequestedAt`) — so a leg that leaves
 *     WITHOUT it can be told apart from a deliberate hang-up and parked.
 *
 * `fk_softphone_device__id` is null for a leg from a client that has not
 * registered a device (no multi-device permission, or a client from before
 * this feature). Such a leg is never parked: the client has no way to
 * come back to it, so its drop keeps the old hang-up behavior.
 *
 * Not auditable: the row is call-control bookkeeping written from Twilio
 * webhooks several times per call, and the call itself already lands in
 * `call` / `call_user`.
 */
@Index('softphone_call_device_leg_call_idx', ['fkCallId'])
@Index('softphone_call_device_leg_call_sid_idx', ['callSid'])
@Index('softphone_call_device_leg_user_status_idx', ['fkUserId', 'status'])
@Index('softphone_call_device_leg_parked_idx', ['status', 'parkedUntil'])
@Entity('softphone_call_device_leg', {}, { auditable: false })
export class SoftphoneCallDeviceLegEntity extends BaseEntity {
	@Column('int', { name: 'fk_call__id', unsigned: true })
	public fkCallId!: number;

	@Column('int', {
		name: 'fk_softphone_device__id',
		unsigned: true,
		nullable: true,
	})
	public fkSoftphoneDeviceId!: number | null;

	/** Tenant-local `user._id` of the rep. */
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	/** Twilio CallSid of this leg. Null while a claim is `pending`. */
	@Column('varchar', { name: 'callSid', length: 64, nullable: true })
	public callSid!: string | null;

	@Column('varchar', { name: 'role', length: 16 })
	public role!: SoftphoneCallDeviceLegRoleModel;

	@Column('varchar', { name: 'status', length: 16 })
	public status!: SoftphoneCallDeviceLegStatusModel;

	@Column('datetime', { name: 'joinedAt', nullable: true })
	public joinedAt!: Date | null;

	@Column('datetime', { name: 'leftAt', nullable: true })
	public leftAt!: Date | null;

	/** Set by the hang-up button before the leg disconnects. A leg that
	 *  leaves with this set was ended on purpose and is never parked. */
	@Column('datetime', { name: 'endRequestedAt', nullable: true })
	public endRequestedAt!: Date | null;

	/** End of the reconnect grace window when the leg was parked. Kept
	 *  after the park resolves as the record that it happened. */
	@Column('datetime', { name: 'parkedUntil', nullable: true })
	public parkedUntil!: Date | null;

	@ManyToOne(() => CallEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_call__id', referencedColumnName: 'id' }])
	public fkCall!: CallEntity;

	@ManyToOne(() => SoftphoneDeviceEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
		nullable: true,
	})
	@JoinColumn([{ name: 'fk_softphone_device__id', referencedColumnName: 'id' }])
	public fkSoftphoneDevice!: SoftphoneDeviceEntity | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;
}
